import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ElectronicTenderOptionDto, PaymentMethod } from '@gc/shared';

import { api } from '@/lib/api';

/**
 * How the money was taken: Cash, Account balance, or one of the venue's
 * switched-on payment providers — FIB, Qi, FastPay (owner, 2026-09-23: make
 * the Payment providers page work).
 *
 * A provider is a tender of its own rather than an "Electronic" button with a
 * second choice behind it: the cashier is looking at the customer's phone
 * showing "FIB" and presses FIB. There is no gateway — the payment is
 * confirmed in the provider's app first, and the picker says so once one is
 * chosen.
 */
export interface Tender {
  method: PaymentMethod;
  /** Set exactly when `method` is ELECTRONIC. */
  providerId: string | null;
  providerLabel: string | null;
}

export const CASH_TENDER: Tender = { method: 'CASH', providerId: null, providerLabel: null };

/**
 * The tender's half of a checkout or refill body. The provider id travels
 * only with ELECTRONIC — the server refuses it on any other method — and a
 * cash or balance body carries exactly the one key it always did.
 */
export function tenderBody(tender: Tender): {
  method: PaymentMethod;
  electronicsProviderId?: string;
} {
  return tender.method === 'ELECTRONIC' && tender.providerId
    ? { method: 'ELECTRONIC', electronicsProviderId: tender.providerId }
    : { method: tender.method };
}

/**
 * The switched-on providers, read once when the dialog opens. A failed read
 * leaves Cash (and balance) working and says so, rather than blocking the
 * till on a list it can settle without.
 */
export function useElectronicTenders(): {
  providers: ElectronicTenderOptionDto[];
  failed: boolean;
} {
  const [providers, setProviders] = useState<ElectronicTenderOptionDto[]>([]);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    api
      .get<ElectronicTenderOptionDto[]>('/electronic-tenders')
      .then((list) => {
        // Anything but a list offers no provider rather than breaking the till.
        if (live) setProviders(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (live) setFailed(true);
      });
    return () => {
      live = false;
    };
  }, []);

  return { providers, failed };
}

export function TenderPicker({
  value,
  onChange,
  providers,
  providersFailed = false,
  allowAccountBalance,
}: {
  value: Tender;
  onChange: (tender: Tender) => void;
  providers: readonly ElectronicTenderOptionDto[];
  providersFailed?: boolean;
  /** The till offers the customer's balance; a refill never can pay itself. */
  allowAccountBalance: boolean;
}) {
  const { t } = useTranslation();

  const options: Array<{ key: string; label: string; tender: Tender }> = [
    { key: 'CASH', label: t('payments.cash'), tender: CASH_TENDER },
    ...(allowAccountBalance
      ? [
          {
            key: 'ACCOUNT_BALANCE',
            label: t('payments.accountBalance'),
            tender: { method: 'ACCOUNT_BALANCE', providerId: null, providerLabel: null } as Tender,
          },
        ]
      : []),
    ...providers.map((provider) => ({
      key: provider.id,
      label: provider.label,
      tender: {
        method: 'ELECTRONIC',
        providerId: provider.id,
        providerLabel: provider.label,
      } as Tender,
    })),
  ];
  const selected = (tender: Tender) =>
    tender.method === value.method && tender.providerId === value.providerId;

  return (
    <div>
      <div
        role="group"
        aria-label={t('tender.label')}
        className="flex flex-wrap overflow-hidden rounded-control border border-subtle bg-raised"
      >
        {options.map((option) => (
          <button
            key={option.key}
            type="button"
            data-tender={option.key}
            onClick={() => onChange(option.tender)}
            aria-pressed={selected(option.tender)}
            className={[
              'min-w-[33%] flex-1 px-3 py-2.5 text-xs transition-colors',
              selected(option.tender)
                ? 'bg-primary font-medium text-primary-fg'
                : 'text-content-secondary hover:bg-hover',
            ].join(' ')}
          >
            {option.label}
          </button>
        ))}
      </div>
      {value.method === 'ELECTRONIC' && value.providerLabel ? (
        <p data-tender-hint className="mt-1.5 text-xs text-ending">
          {t('tender.electronicHint', { provider: value.providerLabel })}
        </p>
      ) : null}
      {providersFailed ? (
        <p className="mt-1.5 text-xs text-content-muted">{t('tender.providersFailed')}</p>
      ) : null}
    </div>
  );
}
